import { useEffect } from "react";
import { useQuery } from "@apollo/client";
import { GET_PENDING_APPROVAL_CARD_COUNT } from "../../../graphql/card";

/**
 * How many card requests are waiting, for the Requests tab's count.
 *
 * Its own query rather than a number lifted out of the table: the count has to
 * be on the tab even while another tab is showing, when the table is not
 * mounted. It selects `pageInfo.total` and nothing else, because that is all it
 * reads; the table's document pulls the whole card node.
 *
 * No `first`. The resolver derives `total` from the rows it returns, so any
 * page size caps the count at that size. It reads right today only because
 * `getCardApprovals` ignores the limit it is handed, which is not a property to
 * build on.
 *
 * `skip` is not optional in practice: `pendingCardApproval` is
 * `@auth(minRole: Manager)`, and /admin/cards has no role guard, so a lower
 * role reaching the page would take an authorization error on every mount.
 * The Requests tab is manager-only too, so nothing is lost by not asking.
 */
export const usePendingRequestCount = (
  refetchIndex?: number,
  { skip = false }: { skip?: boolean } = {},
): number => {
  const { data, refetch } = useQuery(GET_PENDING_APPROVAL_CARD_COUNT, {
    fetchPolicy: "cache-and-network",
    skip,
  });

  // Approving or denying a request changes the queue, and the page bumps this
  // when it does. It cannot ride on `variables`: the server would reject a
  // field it does not take.
  // Guarded on skip as well: refetch on a skipped query runs it anyway.
  useEffect(() => {
    if (refetchIndex && !skip) refetch();
  }, [refetchIndex, refetch, skip]);

  return data?.pendingCardApproval?.pageInfo?.total ?? 0;
};

/** Tab counts stop being useful long before three digits. */
export const formatRequestCount = (count: number): string =>
  count > 99 ? "99+" : String(count);
